var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import pino from 'pino';
import * as clientEnv from 'libs/client-env';
import { sendLogs } from 'api/logging';
import camelcaseKeys from 'camelcase-keys';
import { logStorage } from './log-storage';
const pinoLevels = {
    trace: 10,
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
};
const LOG_SYNC_LIMIT = 50;
const SYNC_INTERVAL = 30000;
const formatMessage = (template, data = {}) => {
    if (typeof template !== 'string') {
        return String(template !== null && template !== void 0 ? template : '');
    }
    return template.replace(/{([^{}]+)}/g, (match, keyPath) => {
        const keys = keyPath.split('.');
        const value = keys.reduce((currentObject, currentKey) => {
            return currentObject != null && typeof currentObject === 'object' ? currentObject[currentKey] : undefined;
        }, data);
        return value !== undefined ? value : match;
    });
};
const normalizeError = (error) => {
    if (error instanceof Error) {
        return Object.assign(Object.assign({}, error), { message: error.message, stack: error.stack });
    }
    if (typeof error === 'object' && error !== null) {
        try {
            return { message: JSON.stringify(error) };
        }
        catch (_a) {
            return { message: '[Unserializable object]' };
        }
    }
    try {
        return { message: String(error) };
    }
    catch (_b) {
        return { message: '[Unrepresentable value]' };
    }
};
export class BeeyLogger {
    constructor(logger) {
        this.logger = logger;
    }
    info(msg, data) {
        this.log('info', { msg, data });
    }
    warn(msg, data) {
        this.log('warn', { msg, data });
    }
    debug(msg, data) {
        this.log('debug', { msg, data });
    }
    trace(msg, data) {
        this.log('trace', { msg, data });
    }
    error(msg, data, error) {
        this.log('error', { msg, data, error });
    }
    child(context) {
        const childLogger = this.logger.child(context);
        return new BeeyLogger(childLogger);
    }
    componentLogger(componentName) {
        return this.child({ component: componentName });
    }
    log(level, payload) {
        const { msg, data, error } = payload;
        if (level === 'error' && error !== undefined) {
            this.logger.error(Object.assign(Object.assign({}, data), { error: normalizeError(error) }), msg);
        }
        else {
            this.logger[level](payload);
        }
    }
}
class LoggerService {
    constructor() {
        var _a, _b;
        this.writeToConsole = (log) => {
            var _a;
            if (log.level > pinoLevels.debug) {
                return;
            }
            const payload = (typeof log.msg === 'object' && log.msg !== null)
                ? log.msg
                : null;
            const messageTemplate = (_a = payload === null || payload === void 0 ? void 0 : payload.msg) !== null && _a !== void 0 ? _a : log.msg;
            const messageData = ((payload === null || payload === void 0 ? void 0 : payload.data) !== undefined && typeof payload.data === 'object')
                ? payload.data
                : {};
            const formattedMessage = formatMessage(messageTemplate, Object.assign(Object.assign({}, log), messageData));
            const cleanedLog = Object.assign({}, log);
            delete cleanedLog.msg;
            // eslint-disable-next-line no-console
            console.debug(formattedMessage, cleanedLog);
        };
        this.sendToServer = (_, logEvent) => {
            var _a, _b, _c;
            if (logEvent.level.value < pinoLevels.info) {
                return;
            }
            const [firstBinding, ...otherBindings] = logEvent.bindings;
            const mergedOtherBindings = otherBindings.reduce((acc, current) => (Object.assign(Object.assign({}, acc), current)), {});
            const { context } = firstBinding;
            const payload = logEvent.messages[0];
            const { msg, data = {} } = payload;
            const mergedData = Object.assign(Object.assign({}, data), context);
            const camelCasedData = camelcaseKeys(mergedData, { deep: true });
            const formattedMessage = formatMessage(msg, camelCasedData);
            const fields = Object.assign(Object.assign({ email: (_a = context === null || context === void 0 ? void 0 : context.userEmail) !== null && _a !== void 0 ? _a : '', userId: (_b = context === null || context === void 0 ? void 0 : context.userId) !== null && _b !== void 0 ? _b : '', pageUrl: (_c = context === null || context === void 0 ? void 0 : context.pageUrl) !== null && _c !== void 0 ? _c : '' }, mergedOtherBindings), data);
            const finalLogForServer = {
                timestamp: new Date().toISOString(),
                level: this.mapPinoLevelToServerLevel(logEvent.level.value),
                message: formattedMessage,
                messageTemplate: typeof msg === 'string' ? msg : '',
                fields,
                exceptions: [],
            };
            void logStorage.saveLogItem(finalLogForServer);
        };
        this.mapPinoLevelToServerLevel = (pinoLevelValue) => {
            if (pinoLevelValue >= pinoLevels.error) {
                return 'Error';
            }
            if (pinoLevelValue >= pinoLevels.warn) {
                return 'Warning';
            }
            if (pinoLevelValue >= pinoLevels.info) {
                return 'Information';
            }
            return 'Verbose';
        };
        this.setupUnloadHandler = () => {
            window.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    void this.syncLogsWithServer();
                }
            });
        };
        const loggerOptions = this.createLoggerOptions();
        const initialContext = {
            context: {
                userEmail: (_a = clientEnv.getUserEmail()) !== null && _a !== void 0 ? _a : undefined,
                userId: (_b = clientEnv.getUserId()) !== null && _b !== void 0 ? _b : undefined,
                pageUrl: window.location.href,
            },
        };
        this.pinoInstance = pino(loggerOptions).child(initialContext);
        this.setupGlobalErrorHandler();
        this.startSyncLoop();
        this.setupUnloadHandler();
    }
    getRoot() {
        return new BeeyLogger(this.pinoInstance);
    }
    createLoggerOptions() {
        return {
            level: 'trace',
            browser: {
                serialize: false,
                asObject: true,
                write: this.writeToConsole,
                transmit: {
                    send: this.sendToServer,
                },
            },
        };
    }
    setupGlobalErrorHandler() {
        window.addEventListener('error', (e) => {
            const errorData = normalizeError(e);
            this.pinoInstance.error({ file: e.filename, error: errorData }, 'Uncaught application error: {error.message}');
        });
    }
    async syncLogsWithServer() {
        if (!navigator.onLine) {
            return;
        }
        try {
            const storedLogs = await logStorage.getAllLogs();
            if (storedLogs.length === 0) {
                return;
            }
            const logsToSend = storedLogs.map((_a) => {
                var { id } = _a, log = __rest(_a, ["id"]);
                return log;
            });
            await sendLogs(logsToSend.slice(0, LOG_SYNC_LIMIT));
            const idsToDelete = storedLogs
                .slice(0, LOG_SYNC_LIMIT)
                .map((log) => log.id);
            await logStorage.deleteLogs(idsToDelete);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to sync logs to the server. Retrying on next interval.', error);
        }
    }
    startSyncLoop() {
        void this.syncLogsWithServer();
        setInterval(() => this.syncLogsWithServer(), SYNC_INTERVAL);
    }
}
export const loggerService = new LoggerService();
